import {
  FC,
  ButtonHTMLAttributes,
  memo,
  ReactNode,
  CSSProperties,
} from 'react';
import styled from 'styled-components';

import { PremierText } from '@components/text';
import Loader from '@components/loader';
import { SKEW_ANGLE } from '@utils/constants';

import TouchableOpacity from './TouchableOpacity';

const StyledSkewedButton = styled(TouchableOpacity).attrs<{
  $angle?: number;
  $isGradient?: boolean;
  $isBorder?: boolean;
}>(({ $angle }) => ({
  style: {
    transform: `skew(${$angle || -SKEW_ANGLE}deg)`,
  },
}))<{
  $angle?: number;
  $isGradient?: boolean;
  $isBorder?: boolean;
}>`
  min-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  background: ${({ theme, $isGradient, $isBorder }) =>
    $isBorder
      ? `linear-gradient(${theme.colors.grey[800]}, ${theme.colors.grey[800]}) padding-box,
              linear-gradient(120deg, ${theme.colors.red[500]} 20%, ${theme.colors.purple[500]} 50%, ${theme.colors.blue[500]} 80%) border-box`
      : $isGradient
        ? `linear-gradient(
    120deg,
    ${theme.colors.red[500]} 20%,
    ${theme.colors.purple[500]} 50%,
    ${theme.colors.blue[500]} 80%
  )`
        : theme.colors.grey[800]};
  border: ${({ theme, $isGradient, $isBorder }) => {
    if ($isBorder) return '2px solid transparent';
    return $isGradient ? 'none' : `0.5px solid ${theme.colors.grey[400]}`;
  }};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);

  &:disabled {
    background: ${({ theme }) => theme.colors.grey[600]};
    border: 0.5px solid ${({ theme }) => theme.colors.grey[400]};
  }
`;

const ButtonContent = styled.div.attrs<{ $angle?: number }>(({ $angle }) => ({
  style: {
    transform: `skew(${$angle || SKEW_ANGLE}deg)`,
  },
}))<{ $angle?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ButtonText = styled(PremierText)<{ $isDisabled?: boolean }>`
  font-size: ${({ theme }) => theme.fonts.fontSize.base};
  color: ${({ theme, $isDisabled }) =>
    $isDisabled ? theme.colors.grey[400] : theme.colors.white};
  line-height: 1.5;
`;

type Props = {
  children?: ReactNode;
  icon?: ReactNode;
  isLoading?: boolean;
  angle?: number;
  isGradient?: boolean;
  isBorder?: boolean;
  textStyle?: CSSProperties;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const SkewedButton: FC<Props> = ({
  children,
  icon,
  disabled = false,
  isLoading = false,
  angle = -10,
  isGradient = true,
  isBorder = false,
  textStyle,
  ...props
}) => {
  return (
    <StyledSkewedButton
      $angle={angle}
      $isGradient={isGradient}
      $isBorder={isBorder}
      disabled={disabled || isLoading}
      {...props}
    >
      <ButtonContent $angle={-angle}>
        {isLoading ? <Loader size={'small'} /> : icon ? icon : null}

        {children &&
          (typeof children === 'string' ? (
            <ButtonText $isDisabled={disabled || isLoading} style={textStyle}>
              {children}
            </ButtonText>
          ) : (
            children
          ))}
      </ButtonContent>
    </StyledSkewedButton>
  );
};

export default memo(SkewedButton);
